import { useEffect, useId, useRef, useSyncExternalStore, type ReactNode } from 'react';

export interface ConfirmOptions {
  title: string;
  /** Large emoji shown above the title. */
  icon?: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` styles the confirm button red (e.g. undo). */
  tone?: 'primary' | 'danger';
}

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

let pending: Pending | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
let hostMounted = false;

/**
 * App-styled replacement for `window.confirm`. Resolves `true` when confirmed.
 * Falls back to the browser dialog if the host isn't mounted.
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  if (!hostMounted) return Promise.resolve(window.confirm(options.title));
  pending?.resolve(false);
  return new Promise((resolve) => {
    pending = { ...options, resolve };
    emit();
  });
}

function settle(ok: boolean) {
  const p = pending;
  pending = null;
  emit();
  p?.resolve(ok);
}

/** Mount once near the app root. */
export function ConfirmHost() {
  const current = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => pending,
  );
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    hostMounted = true;
    return () => {
      hostMounted = false;
    };
  }, []);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (current && !d.open) {
      d.showModal();
      okRef.current?.focus();
    }
    if (!current && d.open) d.close();
  }, [current]);

  const danger = current?.tone === 'danger';
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        settle(false);
      }}
      onClick={(e) => e.target === ref.current && settle(false)}
      className="m-auto w-[min(26rem,calc(100%-2rem))] max-w-none overflow-hidden rounded-3xl bg-cream p-0 text-ink shadow-[0_30px_60px_-20px_rgb(0_0_0/0.7)] ring-1 ring-gold/40 backdrop:bg-felt-950/70"
    >
      {current && (
        <div className="animate-pop">
          <div className="px-6 pt-6 text-center">
            {current.icon && (
              <div className="mx-auto mb-3 grid h-16 w-16 place-items-center rounded-full bg-gold/20 text-4xl ring-4 ring-gold/15" aria-hidden>
                {current.icon}
              </div>
            )}
            <h2 id={titleId} className="font-display text-2xl leading-tight font-extrabold">
              {current.title}
            </h2>
          </div>
          {current.body && <div className="px-6 pt-3 text-center text-sm text-ink-soft">{current.body}</div>}
          <div className="mt-6 grid grid-cols-2 gap-2 border-t border-ink/10 bg-ink/[0.03] p-4">
            <button type="button" className="btn btn-outline-ink" onClick={() => settle(false)}>
              {current.cancelLabel ?? 'Cancel'}
            </button>
            <button
              ref={okRef}
              type="button"
              className={danger ? 'btn bg-suit-red text-white hover:bg-suit-red/90' : 'btn btn-primary'}
              onClick={() => settle(true)}
            >
              {current.confirmLabel}
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
